/**
 * What the viewer may see on the unified dashboard — by capability, not by
 * role (#1337, E1.5). The UI only decides what to OFFER: every read is
 * refused or projected by the server by the same policy («Trust the
 * Frontend», SECURITY.md).
 *
 * The capabilities come from «my access» (`/access/me`) through the screen
 * subject both shells provide:
 * - `legacy` — the role decides, and every capability passes (the answer lists
 *   all of them);
 * - `managed` — the grant's list, and nothing else;
 * - not known yet or not asked — the role decides meanwhile (`AccessState`),
 *   so everything; the shell waits for a read in flight and covers the screen
 *   when the read failed (`accessUnchecked`).
 */
import {
  ANALYTICS_CAPABILITIES,
  type AnalyticsCapability,
} from '@aica/api-types/schemas';
import type { ScreenSubject } from '@/lib/route-permissions';

export type CapabilitySet = ReadonlySet<AnalyticsCapability>;

const ALL: CapabilitySet = new Set(ANALYTICS_CAPABILITIES);

export function viewerCapabilities(subject: ScreenSubject | null): CapabilitySet {
  if (!subject || subject.isOwner) return ALL;
  const { access } = subject;
  if (access.status !== 'resolved') return ALL;
  return new Set(access.access.capabilities);
}

/**
 * «Не удалось проверить права» (503 on `/access/me`, after its retries):
 * nobody knows whether the viewer holds a grant, so no number is drawn — the
 * whole screen offers to ask again.
 */
export function accessUnchecked(subject: ScreenSubject | null): boolean {
  return (
    !!subject &&
    !subject.isOwner &&
    subject.access.status === 'failed' &&
    !!subject.access.retry
  );
}

/** A block's requirement: all of `requires`, and one of `requiresAny` when given. */
export interface CapabilityRequirement {
  requires: readonly AnalyticsCapability[];
  requiresAny?: readonly AnalyticsCapability[];
}

export function meets(caps: CapabilitySet, need: CapabilityRequirement): boolean {
  return (
    need.requires.every((cap) => caps.has(cap)) &&
    (!need.requiresAny || need.requiresAny.some((cap) => caps.has(cap)))
  );
}
